import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, BookOpen, Clock, Eye, Tag, Sparkles, Plus, Pencil, Trash2, X, Check
} from "lucide-react";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "exercise", label: "Exercise" },
  { id: "cognitive", label: "Cognitive" },
  { id: "nutrition", label: "Nutrition" },
  { id: "sleep", label: "Sleep" },
  { id: "mindfulness", label: "Mindfulness" },
  { id: "resilience", label: "Resilience" },
];

const CATEGORY_COLORS = {
  exercise: "bg-emerald-100 text-emerald-700",
  cognitive: "bg-indigo-100 text-indigo-700",
  nutrition: "bg-amber-100 text-amber-700",
  sleep: "bg-blue-100 text-blue-700",
  mindfulness: "bg-pink-100 text-pink-700",
  resilience: "bg-purple-100 text-purple-700",
  general: "bg-slate-100 text-slate-700",
};

// Article Detail View
const ArticleDetail = ({ postId, onBack }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${postId}`);
        setPost(res.data);
      } catch (error) {
        toast.error("Article not found");
        onBack();
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [postId, onBack]);

  if (loading) {
    return <div className="text-center py-12"><div className="w-8 h-8 rounded-full bg-sky-400/30 glow-pulse mx-auto" /></div>;
  }

  if (!post) return null;

  const catColor = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general;

  return (
    <div className="animate-in fade-in duration-300" data-testid="article-detail">
      <Button variant="ghost" onClick={onBack} className="text-sky-600 mb-4" data-testid="article-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Articles
      </Button>

      <article className="glass-card p-6 md:p-10">
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-xs px-2.5 py-1 rounded-full ${catColor}`}>{post.category}</span>
          {post.ai_generated && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-sky-100 text-sky-600 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI Research
            </span>
          )}
        </div>

        <h1 className="text-2xl md:text-3xl font-light text-sky-900 mb-4" style={{ fontFamily: "Outfit" }}>
          {post.title}
        </h1>

        <div className="flex items-center gap-4 text-xs text-slate-400 mb-8 pb-6 border-b border-sky-100">
          <span>{post.author_name}</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" /> {post.views} views
          </span>
        </div>

        <div className="prose prose-slate prose-sm max-w-none leading-relaxed text-slate-700 whitespace-pre-line">
          {post.content}
        </div>

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-sky-100">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                <Tag className="w-3 h-3 inline mr-1" />{tag}
              </span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

// Admin Post Editor
const PostEditor = ({ onSave, onCancel, editPost }) => {
  const [title, setTitle] = useState(editPost?.title || "");
  const [content, setContent] = useState(editPost?.content || "");
  const [summary, setSummary] = useState(editPost?.summary || "");
  const [category, setCategory] = useState(editPost?.category || "general");
  const [tagsStr, setTagsStr] = useState(editPost?.tags?.join(", ") || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      const data = {
        title: title.trim(),
        content: content.trim(),
        summary: summary.trim() || content.trim().slice(0, 200),
        category,
        tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
        published: true,
      };

      if (editPost?.id) {
        await axios.put(`${API}/blog/posts/${editPost.id}`, data);
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/blog/posts`, data);
        toast.success("Article published");
      }
      onSave();
    } catch (error) {
      toast.error("Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-card p-6 space-y-4 animate-in fade-in duration-200" data-testid="post-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-sky-800">{editPost?.id ? "Edit Article" : "New Article"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
      </div>

      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Article title..."
        className="w-full p-3 rounded-xl border border-sky-200 outline-none focus:ring-2 focus:ring-sky-300 text-sky-900"
        data-testid="editor-title"
      />

      <textarea
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        placeholder="Brief summary (optional)..."
        className="w-full p-3 rounded-xl border border-sky-200 outline-none focus:ring-2 focus:ring-sky-300 text-sky-900 h-16 resize-none"
        data-testid="editor-summary"
      />

      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Write your article content..."
        className="w-full p-3 rounded-xl border border-sky-200 outline-none focus:ring-2 focus:ring-sky-300 text-sky-900 h-64 resize-y"
        data-testid="editor-content"
      />

      <div className="flex gap-3">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-3 py-2 rounded-xl border border-sky-200 outline-none text-sm text-sky-800"
          data-testid="editor-category"
        >
          {CATEGORIES.filter((c) => c.id).map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <input
          type="text"
          value={tagsStr}
          onChange={(e) => setTagsStr(e.target.value)}
          placeholder="Tags (comma-separated)"
          className="flex-1 px-3 py-2 rounded-xl border border-sky-200 outline-none text-sm text-sky-800"
          data-testid="editor-tags"
        />
      </div>

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={saving} className="glow-button" data-testid="editor-save-btn">
          <Check className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Publish"}
        </Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

const Blog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [viewingPost, setViewingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check auth optionally (public page)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setIsLoggedIn(true);
        setIsAdmin(res.data?.is_admin === true);
      } catch (error) {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      const params = category ? `?category=${category}` : "";
      const res = await axios.get(`${API}/blog/posts${params}`);
      setPosts(res.data.posts);
    } catch (error) {
      devError("Blog fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/blog/generate`);
      toast.success("New science article generated!");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to generate article");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/blog/posts/${postId}`);
      toast.success("Article deleted");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  if (viewingPost) {
    return (
      <div className="min-h-screen bg-sky-50">
        <div className="fixed top-0 left-0 w-64 h-64 bg-indigo-300/10 rounded-full blur-3xl pointer-events-none" />
        <main className="pt-8 pb-16 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <ArticleDetail postId={viewingPost} onBack={() => setViewingPost(null)} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="blog-page">
      <div className="fixed top-0 left-0 w-64 h-64 bg-indigo-300/10 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(isLoggedIn ? "/dashboard" : "/")}
              className="text-sky-600 hover:text-sky-800"
              data-testid="blog-back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {isLoggedIn ? t("common.dashboard") : "Home"}
            </Button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-500" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">Science & Wellness</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-light text-sky-900 mb-3" style={{ fontFamily: "Outfit" }}>
              Science & Wellness
            </h1>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Evidence-based insights from peer-reviewed research on fitness, cognitive health, and emotional resilience.
            </p>
          </div>

          {/* Admin Actions */}
          {isAdmin && (
            <div className="flex flex-wrap gap-2 mb-6">
              <Button
                onClick={() => setShowEditor(true)}
                variant="outline"
                className="text-sky-700"
                data-testid="new-post-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Write Article
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                variant="outline"
                className="text-indigo-600"
                data-testid="generate-post-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {generating ? "Generating..." : "Generate Science Article"}
              </Button>
            </div>
          )}

          {/* Editor */}
          {showEditor && isAdmin && (
            <div className="mb-6">
              <PostEditor
                onSave={() => { setShowEditor(false); fetchPosts(); }}
                onCancel={() => setShowEditor(false)}
              />
            </div>
          )}

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-categories">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  category === cat.id
                    ? "bg-sky-500 text-white shadow-sm"
                    : "bg-white/60 text-slate-500 hover:text-sky-700 hover:bg-white/90"
                }`}
                data-testid={`cat-${cat.id || "all"}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12"><div className="w-8 h-8 rounded-full bg-sky-400/30 glow-pulse mx-auto" /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-sky-200 mx-auto mb-4" strokeWidth={1} />
              <h3 className="text-lg font-light text-sky-800 mb-2" style={{ fontFamily: "Outfit" }}>No articles yet</h3>
              <p className="text-sm text-slate-400 mb-4">Generate your first science article or write one yourself</p>
              {isAdmin && (
                <Button onClick={handleGenerate} disabled={generating} className="glow-button" data-testid="first-generate-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> Generate First Article
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-grid">
              {posts.map((post) => {
                const catColor = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general;
                return (
                  <article
                    key={post.id}
                    className="glass-card glass-card-hover p-6 cursor-pointer group"
                    onClick={() => setViewingPost(post.id)}
                    data-testid={`blog-post-${post.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${catColor}`}>{post.category}</span>
                      {post.ai_generated && (
                        <span className="text-xs text-sky-500 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> AI
                        </span>
                      )}
                    </div>

                    <h2 className="text-lg font-medium text-sky-800 group-hover:text-sky-600 transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-sm text-slate-500 line-clamp-3 mb-4">{post.summary}</p>

                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <div className="flex items-center gap-3">
                        <span>{post.author_name}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(post.id); }}
                          className="text-slate-300 hover:text-red-400 transition-colors"
                          data-testid={`delete-post-${post.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Blog;
